import React, { useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import { useDraggable } from '@dnd-kit/core'
import { DashboardCell } from '../../../types/gotypes'
import { CellRef } from '../../../utils/dashboardBuilderLayout'
import { WIDGET_TYPE_OPTIONS } from '../widgetTaxonomy'
import WidgetPlaceholder, { sidebarTypeForCell } from './WidgetPlaceholder'

interface BuilderCanvasCellProps {
  cell: DashboardCell
  cellRef: CellRef
  onDuplicate: (ref: CellRef) => void
  onDelete: (ref: CellRef) => void
}

/** One placed widget on the builder canvas: draggable (to reorder / move columns), with a hover toolbar. */
const BuilderCanvasCell: React.FC<BuilderCanvasCellProps> = ({ cell, cellRef, onDuplicate, onDelete }) => {
  const [hovered, setHovered] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cell-${cellRef.rowIndex}-${cellRef.cellIndex}`,
    data: { source: 'canvas', ref: cellRef },
  })

  const sidebarType = sidebarTypeForCell(cell.widget_type, cell.table_graph_data?.view_mode)
  const label = WIDGET_TYPE_OPTIONS[sidebarType]?.label || cell.widget_type

  return (
    <Box
      ref={setNodeRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        border: '1px solid',
        borderColor: hovered ? 'info.main' : 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        opacity: isDragging ? 0.35 : 1,
        overflow: 'hidden',
        transition: 'border-color 120ms ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          {...listeners}
          {...attributes}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'grab', flex: 1, minWidth: 0 }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25, opacity: hovered ? 1 : 0, transition: 'opacity 120ms ease' }}>
          <Tooltip title="Duplicate">
            <IconButton size="small" aria-label="Duplicate" onClick={() => onDuplicate(cellRef)}>
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" aria-label="Delete" onClick={() => onDelete(cellRef)}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <WidgetPlaceholder widgetType={cell.widget_type} viewMode={cell.table_graph_data?.view_mode} />
    </Box>
  )
}

export default BuilderCanvasCell
